import { useState, useEffect, useCallback } from 'react';
import { useContent } from '../hooks/useContent';
import { useToast } from '../hooks/useToast';
import { useGlobalPreview } from '../hooks/useGlobalPreview';
import { getContentId } from '../types';

/** Map toggle field names to section names used in data-cms-section attributes */
const fieldToSection: Record<string, string> = {
  showProducts: 'products',
  showBlog: 'blog',
  showAbout: 'about',
  showContact: 'contact',
  showGallery: 'gallery',
  showTestimonials: 'testimonials',
  showHours: 'hours',
  showMenu: 'menu',
};

interface SectionToggleProps {
  siteId: string;
  field: string;
  label: string;
}

/**
 * Toggle switch for enabling/disabling an optional site section.
 * Reads and writes the boolean field on the site content singleton.
 */
export default function SectionToggle({ siteId, field, label }: SectionToggleProps) {
  const { items, loading, update } = useContent(siteId, 'site');
  const { addToast } = useToast();
  const { toggleSection } = useGlobalPreview();
  const [toggling, setToggling] = useState(false);
  const [enabled, setEnabled] = useState(false);

  const siteItem = items[0];

  useEffect(() => {
    if (siteItem) {
      const data = siteItem.data as Record<string, unknown>;
      setEnabled(data[field] === true || data[field] === 'true');
    }
  }, [siteItem, field]);

  const handleToggle = useCallback(async () => {
    if (!siteItem || toggling) return;
    const newValue = !enabled;
    setEnabled(newValue);
    setToggling(true);
    try {
      const id = getContentId(siteItem.contentKey);
      const updatedData = { ...(siteItem.data as Record<string, unknown>), [field]: newValue };
      await update(id, updatedData);
      // Send toggle to preview overlay
      const section = fieldToSection[field];
      if (section) {
        toggleSection(section, newValue);
      }
      addToast('success', newValue ? `${label} enabled` : `${label} disabled`);
    } catch (err) {
      setEnabled(!newValue); // revert
      addToast('error', err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setToggling(false);
    }
  }, [siteItem, toggling, enabled, field, label, update, addToast, toggleSection]);

  if (loading || !siteItem) return null;

  return (
    <div className="section-toggle">
      <span className="section-toggle-label">
        {enabled ? '✓ Visible on site' : 'Hidden from site'}
      </span>
      <button
        className={`section-toggle-switch ${enabled ? 'active' : ''}`}
        onClick={handleToggle}
        disabled={toggling}
        title={enabled ? `Disable ${label}` : `Enable ${label}`}
        role="switch"
        aria-checked={enabled}
      >
        <span className="section-toggle-knob" />
      </button>
    </div>
  );
}
